import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import AuthService from '../../services/AuthService';
import { API_CONFIG } from '../../utils/constants';

const LoginModal = ({ onClose, onSwitchToRegister }) => {
  const { authStart, authSuccess, authError } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const googleButtonRef = useRef(null);
  const errorClasses = "text-red-500 text-[10px] mt-1 font-bold uppercase tracking-wide pl-1";

  useEffect(() => {
    const initializeGoogle = () => {
      if (window.google && API_CONFIG.GOOGLE_CLIENT_ID) {
        try {
          window.google.accounts.id.initialize({
            client_id: API_CONFIG.GOOGLE_CLIENT_ID,
            callback: handleGoogleCallback,
            auto_select: false,
            cancel_on_tap_outside: true,
          });

          if (googleButtonRef.current) {
            window.google.accounts.id.renderButton(googleButtonRef.current, {
              theme: 'filled_black',
              size: 'large',
              width: '100%',
              text: 'continue_with',
              shape: 'pill',
            });
          }
        } catch (error) {
          console.error('Google Sign-In initialization error:', error);
        }
      }
    };

    if (window.google) {
      initializeGoogle();
    } else {
      const checkGoogle = setInterval(() => {
        if (window.google) {
          clearInterval(checkGoogle);
          initializeGoogle();
        }
      }, 100);
      setTimeout(() => clearInterval(checkGoogle), 10000);
    }
  }, []);

  const handleGoogleCallback = async (response) => {
    if (!response.credential) return;
    setGoogleLoading(true);
    authStart();
    try {
      const result = await AuthService.googleLogin(response.credential);
      authSuccess(result.user, result.access_token, result.refresh_token);
      onClose();
    } catch (error) {
      authError(error.message || 'Google Sign-In failed');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = 'Email required';
    if (!formData.password) newErrors.password = 'Password required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    authStart();
    try {
      const response = await AuthService.login(formData.email, formData.password, formData.rememberMe);
      authSuccess(response.user, response.access_token, response.refresh_token);
      onClose();
    } catch (error) {
      authError(error.message || 'Login failed');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="glass-panel max-w-md w-full rounded-[2.5rem] overflow-hidden border-white/10 shadow-2xl animate-in fade-in zoom-in duration-300">
        {/* Header */}
        <div className="p-8 pb-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-black text-white tracking-tighter uppercase">Sign In</h2>
            <p className="text-[10px] text-blue-500 font-black uppercase tracking-widest mt-1">LankaWayFinder Chauffeur</p>
          </div>
          <button onClick={onClose} className="p-2 bg-white/5 border border-white/10 rounded-full text-white hover:bg-white/10 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="p-8 pt-4">
          {/* Google */}
          <div className="mb-6">
            <div ref={googleButtonRef} className="w-full" style={{ minHeight: '44px' }} />
            {googleLoading && (
              <div className="flex items-center justify-center mt-3 gap-2">
                <div className="w-3 h-3 border-2 border-white/10 border-t-blue-500 rounded-full animate-spin"></div>
                <span className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Verifying...</span>
              </div>
            )}
          </div>

          <div className="relative mb-8 text-center">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/5"></div></div>
            <span className="relative px-3 bg-[#0a0f18] text-[9px] text-gray-500 font-black uppercase tracking-widest">Or secure login</span>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2 ml-1">Email</label>
              <input
                type="email" name="email" value={formData.email} onChange={handleInputChange}
                className={`w-full bg-white/5 border rounded-2xl px-5 py-3.5 text-white text-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none transition-all ${errors.email ? 'border-red-500/50 bg-red-500/5' : 'border-white/10'}`}
                placeholder="name@example.com"
              />
              {errors.email && <p className={errorClasses}>{errors.email}</p>}
            </div>

            <div>
              <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2 ml-1">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleInputChange}
                  className={`w-full bg-white/5 border rounded-2xl px-5 py-3.5 text-white text-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none transition-all ${errors.password ? 'border-red-500/50 bg-red-500/5' : 'border-white/10'}`}
                  placeholder="••••••••"
                />
                {errors.password && <p className={errorClasses}>{errors.password}</p>}
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors">
                  {showPassword ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L7.05 7.05m2.828 2.828L12 12m-3.122-3.122l2.828 2.828m.707-2.828l2.828 2.828M2.929 5.636l16.97 16.97" /></svg>
                  : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between px-1">
              <label className="flex items-center gap-2 cursor-pointer group">
                <input type="checkbox" name="rememberMe" checked={formData.rememberMe} onChange={handleInputChange} className="hidden" />
                <div className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${formData.rememberMe ? 'bg-blue-600 border-blue-600' : 'border-white/20'}`}>
                  {formData.rememberMe && <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20"><path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" /></svg>}
                </div>
                <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest group-hover:text-gray-300 transition-colors">Remember me</span>
              </label>
              <button type="button" className="text-[10px] font-black text-blue-500 uppercase tracking-widest hover:text-white transition-colors">Forgot Password?</button>
            </div>

            <button type="submit" className="w-full py-4 bg-blue-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-blue-700 transition-all shadow-xl shadow-blue-500/20 active:scale-95">Sign Into Account</button>
          </form>

          <div className="mt-8 text-center pt-6 border-t border-white/5">
            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">
              New to LankaWayFinder?{' '}
              <button onClick={onSwitchToRegister} className="text-blue-500 hover:text-white transition-colors">Join Premium Network</button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;